import React, { FC } from 'react'
import { getValueListBgColor, getBgThemeBasedOnColor } from '../../../../../utils/background-util'
import {
  getCWPEnvironmentForURL,
  APIENDPOINT,
  sanitizeHTML,
} from '../../../../../utils/common-utils'
import { TwoColumnBannerProps } from './TwoColumnBanner.types'
import { BrandIcon } from '../../../../Compounds/src/BrandIconCompound'
import { LinkCompound } from '../../../../Compounds/src/LinkCompound'

const renderColumn = (columnProps: any) => {
  const {
    testId,
    icon,
    iconColor,
    iconSize,
    iconIdentifier,
    iconStyles,
    eyebrowImage,
    eyebrowImageAlt,
    eyebrowText,
    columnContent,
    hasImage,
    primaryBtn,
    tertiaryBtn,
    columnClasses,
  } = columnProps

  return (
    <div
      className={`TwoColumnBanner-column u-sm-sizeFull u-paddingVert0 u-paddingHoriz0 ${columnClasses}`}
      data-testid={testId ? `${testId}-columnSection` : null}
    >
      <div className={`${hasImage ? 'TwoColumnBanner-fullHeight' : ''}`}>
        {icon && (
          <div>
            <BrandIcon
              iconsDropdown={icon}
              iconsColorsDropdown={iconColor}
              size={iconSize}
              identifier={iconIdentifier}
              stylesDropdown={iconStyles}
            />
          </div>
        )}
        {eyebrowImage && (
          <div className='u-paddingBottom20'>
            <img src={eyebrowImage} alt={eyebrowImageAlt} />
          </div>
        )}
        {eyebrowText && <p className='Type-5 u-textEyebrow u-paddingVert8'>{eyebrowText}</p>}
        <div
          className={`TwoColumnBanner-innerContent ${hasImage ? 'TwoColumnBanner-fullHeight' : ''}`}
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(columnContent) }}
        />
        {(primaryBtn || tertiaryBtn) && (
          <div className='u-flex u-sm-flexCol u-paddingTop32'>
            {primaryBtn || null}
            {tertiaryBtn || null}
          </div>
        )}
      </div>
    </div>
  )
}

const getColumnClasses = (
  column?: string,
  leftContentHasImage?: boolean,
  rightContentHasImage?: boolean
) => {
  let defaultClasses = `u-sm-paddingHoriz16 u-md-paddingHoriz20 `
  let contentHasImage = `u-sm-paddingBottom40 u-md-paddingBottom40 u-flexAlignContentCenter`
  let contentHasNoImage = ` u-sm-paddingBottom40 u-md-paddingBottom40 u-flexAlignContentStart`

  if (column === 'left') {
    if (leftContentHasImage) return defaultClasses + contentHasImage
    if (!leftContentHasImage && rightContentHasImage)
      return defaultClasses + 'u-flexAlignContentCenter'
    if (!leftContentHasImage && !rightContentHasImage) return defaultClasses + contentHasNoImage
  }
  if (column === 'right') {
    if (rightContentHasImage) return defaultClasses + contentHasImage
    if (!rightContentHasImage && leftContentHasImage)
      return defaultClasses + 'u-flexAlignContentCenter'
  }
  return defaultClasses.trim()
}

const isBgDark = (color: string) => {
  return ['purple', 'midnight-blue'].includes(color)
}

const getColorClasses = (bgColor: any, textColor: any) => {
  let bgClass = ''
  let isDark
  if (bgColor) {
    bgClass = getValueListBgColor(bgColor)
    isDark = isBgDark(bgColor)
  }
  const contrastClass = textColor === 'white' || isDark ? 'Content--reversed' : ''
  return `${bgClass} ${contrastClass}`
}

export const TwoColumnBanner: FC<TwoColumnBannerProps> = props => {
  const env = getCWPEnvironmentForURL(window.location.href)
  const galleryPath = APIENDPOINT(env).baseGallery
  const svg = `${galleryPath}content/gallery/ampmicrosites/brand-icons`
  const hasFacet = props?.content?.bgColor?.includes('facet')

  const { content, testId } = props
  const {
    verticalSpacing,
    leftIcon: {
      iconsDropdown: leftIcon,
      iconsColorsDropdown: leftIconColor,
      stylesDropdown: leftIconStyles,
      size: leftIconSize,
      identifier: leftIconIdentifier,
      enableBorder: leftIconEnableBorder = content.leftIcon?.enableBorder === 'true',
    } = {},
    leftEyebrow: { alt: leftEyebrowImageAlt, src: leftEyebrowImage } = {},
    leftEyebrowText,
    leftColumn,
    leftLink,
    svgUrl = svg,
    rightIcon: {
      iconsDropdown: rightIcon,
      iconsColorsDropdown: rightIconColor,
      size: rightIconSize,
      identifier: rightIconIdentifier,
      stylesDropdown: rightIconStyles,
      enableBorder: rightIconEnableBorder = content.leftIcon?.enableBorder === 'true',
    } = {},
    rightEyebrow: { src: rightEyebrowImage, alt: rightEyebrowImageAlt } = {},
    rightEyebrowText,
    rightColumn,
    rightLink,
    brandExtension,
    twoColumnId,
  } = content

  const backgroundImage =
    content?.bgColor && hasFacet
      ? `${galleryPath}content/gallery/ampmicrosites/facets/banner-facet-${
          props?.content?.bgColor?.replace('-facet', '') || 'midnightblue'
        }.svg`
      : ''
  const backgroundColor = props.content?.bgColor && !hasFacet ? props.content.bgColor : ''
  const textColor =
    getBgThemeBasedOnColor(props.content?.bgColor as string) === 'Dark' ? 'white' : 'black'

  const leftContentHasImage = leftColumn?.content?.includes('<img')
  const rightContentHasImage = rightColumn?.content?.includes('<img')

  const leftPrimaryBtn = leftLink?.[0]?.href && (
    <LinkCompound
      {...leftLink?.[0]}
      classes='u-sm-block u-marginRight8 u-sm-marginRight0 u-sm-marginBottom8'
    />
  )
  const leftTertiaryBtn = leftLink?.[1]?.href && (
    <LinkCompound {...leftLink?.[1]} classes='u-sm-block' />
  )
  const rightPrimaryBtn = rightLink?.[0]?.href && (
    <LinkCompound
      {...rightLink?.[0]}
      classes='u-sm-block u-marginRight8 u-sm-marginRight0 u-sm-marginBottom8'
    />
  )
  const rightTertiaryBtn = rightLink?.[1]?.href && (
    <LinkCompound {...rightLink?.[1]} classes='u-sm-block' />
  )

  return (
    <>
      <style
        type='text/css'
        dangerouslySetInnerHTML={{
          __html: `
          [id="${twoColumnId}"] .TwoColBgdImage { background: ${
            backgroundImage ? `url(${backgroundImage})` : null
          }; }
      `,
        }}
      />
      <section
        className={`TwoColumnBanner TwoColBgdImage u-flex u-flexAlignItemsStretch u-flexAlignItemsCenter ${getColorClasses(
          backgroundColor,
          textColor
        )} ${verticalSpacing} ${brandExtension}`}
        data-testid={testId ? `${testId}-backgroundImage` : null}
      >
        <div
          className={`Content Grid TwoColumnBanner--Gap u-sm-flexCol u-md-flexCol ${
            rightContentHasImage ? 'u-sm-flexColReverse u-md-flexColReverse' : ''
          } u-paddingVert32 u-sm-paddingVert32 u-md-paddingVert32 u-sizeConstrained`}
        >
          {renderColumn({
            testId: testId ? `${testId}-left` : null,
            icon: leftIcon,
            iconColor: leftIconColor,
            iconSize: leftIconSize,
            iconIdentifier: leftIconIdentifier,
            iconStyles: leftIconStyles,
            svgUrl: svgUrl,
            eyebrowImage: leftEyebrowImage,
            eyebrowImageAlt: leftEyebrowImageAlt,
            eyebrowText: leftEyebrowText,
            columnContent: leftColumn?.content,
            hasImage: leftContentHasImage,
            primaryBtn: leftPrimaryBtn,
            tertiaryBtn: leftTertiaryBtn,
            columnClasses: getColumnClasses('left', leftContentHasImage, rightContentHasImage),
          })}
          {renderColumn({
            testId: testId ? `${testId}-right` : null,
            icon: rightIcon,
            iconColor: rightIconColor,
            iconSize: rightIconSize,
            iconIdentifier: rightIconIdentifier,
            iconStyles: rightIconStyles,
            svgUrl: svgUrl,
            eyebrowImage: rightEyebrowImage,
            eyebrowImageAlt: rightEyebrowImageAlt,
            eyebrowText: rightEyebrowText,
            columnContent: rightColumn?.content,
            hasImage: rightContentHasImage,
            primaryBtn: rightPrimaryBtn,
            tertiaryBtn: rightTertiaryBtn,
            columnClasses: getColumnClasses('right', leftContentHasImage, rightContentHasImage),
          })}
        </div>
      </section>
    </>
  )
}
